import { BOOTSTRAP_QUERY_STALE_TIME, queryClient } from '@/utils/reactQueryClient';
import { createChainAnalyticsController, createCourseAnalyticsController } from '@saas/api-client';
import { globalAuthRequestFunction } from '../http/request';
import { makeQuery } from '../http/queryUtils';

const chainAnalyticsController = createChainAnalyticsController(globalAuthRequestFunction);
const courseAnalyticsController = createCourseAnalyticsController(globalAuthRequestFunction);

// --- Keys factory ---

const chainAnalyticsServiceKeys = {
	base: 'CHAIN_ANALYTICS',
	overview: (params: Parameters<typeof chainAnalyticsController.getOverview>[0]) =>
		[chainAnalyticsServiceKeys.base, 'overview', params] as const,
	companyRankings: (params: Parameters<typeof chainAnalyticsController.getCompanyRankings>[0]) =>
		[chainAnalyticsServiceKeys.base, 'company-rankings', params] as const,
	capacity: (params: Parameters<typeof chainAnalyticsController.getCapacity>[0]) =>
		[chainAnalyticsServiceKeys.base, 'capacity', params] as const,
	revenue: (params: Parameters<typeof chainAnalyticsController.getRevenue>[0]) =>
		[chainAnalyticsServiceKeys.base, 'revenue', params] as const,
	geographic: (params: Parameters<typeof chainAnalyticsController.getGeographic>[0]) =>
		[chainAnalyticsServiceKeys.base, 'geographic', params] as const,
	ratings: (params: Parameters<typeof chainAnalyticsController.getRatings>[0]) =>
		[chainAnalyticsServiceKeys.base, 'ratings', params] as const,
	courseAnalytics: (params: Parameters<typeof courseAnalyticsController.getChainCourseAnalytics>[0]) =>
		[chainAnalyticsServiceKeys.base, 'course-analytics', params] as const,
	courseAnalyticsDetail: (params: Parameters<typeof courseAnalyticsController.getCourseAnalytics>[0]) =>
		[chainAnalyticsServiceKeys.base, 'course-analytics-detail', params] as const,
};

// --- Invalidation map ---

const invalidateChainAnalyticsQueries = {
	all: () => queryClient.invalidateQueries({ queryKey: [chainAnalyticsServiceKeys.base] }),
	overview: () => queryClient.invalidateQueries({ queryKey: [chainAnalyticsServiceKeys.base, 'overview'] }),
	companyRankings: () =>
		queryClient.invalidateQueries({ queryKey: [chainAnalyticsServiceKeys.base, 'company-rankings'] }),
	capacity: () => queryClient.invalidateQueries({ queryKey: [chainAnalyticsServiceKeys.base, 'capacity'] }),
	revenue: () => queryClient.invalidateQueries({ queryKey: [chainAnalyticsServiceKeys.base, 'revenue'] }),
	geographic: () => queryClient.invalidateQueries({ queryKey: [chainAnalyticsServiceKeys.base, 'geographic'] }),
	ratings: () => queryClient.invalidateQueries({ queryKey: [chainAnalyticsServiceKeys.base, 'ratings'] }),
	courseAnalytics: () =>
		queryClient.invalidateQueries({ queryKey: [chainAnalyticsServiceKeys.base, 'course-analytics'] }),
};

// --- Queries ---

// Prefetched over the home's default date range in the dashboard loader; pinned so the home reads it instead of refetching on mount.
const useGetOverview = makeQuery({
	queryFunction: chainAnalyticsController.getOverview,
	queryKeyFactory: (params) => [...chainAnalyticsServiceKeys.overview(params)],
	defaultOptions: { staleTime: BOOTSTRAP_QUERY_STALE_TIME },
});

const useGetCompanyRankings = makeQuery({
	queryFunction: chainAnalyticsController.getCompanyRankings,
	queryKeyFactory: (params) => [...chainAnalyticsServiceKeys.companyRankings(params)],
});

const useGetCapacity = makeQuery({
	queryFunction: chainAnalyticsController.getCapacity,
	queryKeyFactory: (params) => [...chainAnalyticsServiceKeys.capacity(params)],
});

const useGetRevenue = makeQuery({
	queryFunction: chainAnalyticsController.getRevenue,
	queryKeyFactory: (params) => [...chainAnalyticsServiceKeys.revenue(params)],
});

const useGetGeographic = makeQuery({
	queryFunction: chainAnalyticsController.getGeographic,
	queryKeyFactory: (params) => [...chainAnalyticsServiceKeys.geographic(params)],
});

const useGetRatings = makeQuery({
	queryFunction: chainAnalyticsController.getRatings,
	queryKeyFactory: (params) => [...chainAnalyticsServiceKeys.ratings(params)],
});

const useGetCourseAnalytics = makeQuery({
	queryFunction: courseAnalyticsController.getChainCourseAnalytics,
	queryKeyFactory: (params) => [...chainAnalyticsServiceKeys.courseAnalytics(params)],
	defaultOptions: { staleTime: BOOTSTRAP_QUERY_STALE_TIME },
});

const useGetCourseAnalyticsDetail = makeQuery({
	queryFunction: courseAnalyticsController.getCourseAnalytics,
	queryKeyFactory: (params) => [...chainAnalyticsServiceKeys.courseAnalyticsDetail(params)],
});

// --- Export ---

export const chainAnalyticsService = {
	useGetOverview,
	useGetCompanyRankings,
	useGetCapacity,
	useGetRevenue,
	useGetGeographic,
	useGetRatings,
	useGetCourseAnalytics,
	useGetCourseAnalyticsDetail,
	exportCsv: chainAnalyticsController.exportCsv,
	exportCourseAnalytics: courseAnalyticsController.exportCourseAnalytics,
	keys: chainAnalyticsServiceKeys,
	invalidate: invalidateChainAnalyticsQueries,
};
